import {
  Firestore,
  deleteField,
  doc,
  writeBatch
} from 'firebase/firestore';

import type { NovelStorageLayout, SectionDefinition } from '@/lib/types';

import { fetchWithCache } from '@/firebase/firestore/cache';
import { StorageResolver } from '@/firebase/firestore/storage-resolver';

const mergeOptions = { merge: true };

export async function loadEntitySettings(
  firestore: Firestore,
  ownerId: string,
  novelId: string,
  layout: NovelStorageLayout,
  entityType: string,
  categoryNameFallback?: string
): Promise<SectionDefinition[]> {
  const resolver = new StorageResolver(firestore);
  const metaMode = layout?.entities?.[entityType]?.meta || 'embedded';

  if (metaMode === 'split') {
    const metaRef = doc(firestore, 'users', ownerId, 'novels', novelId, 'entitiesMeta', entityType);
    const metaSnap = await fetchWithCache(metaRef);
    if (metaSnap.exists()) {
      const data = (metaSnap.data() as { settings?: SectionDefinition[] }).settings || [];
      if (data.length > 0) return data;
    }
  }
  const novelRef = resolver.getNovelRef(ownerId, novelId);
  const novelSnap = await fetchWithCache(novelRef);
  if (!novelSnap.exists()) return [];

  const novelData = novelSnap.data() as {
    entitiesMeta?: {
      settings?: Record<string, SectionDefinition[]>;
      codexCategories?: Record<string, string>;
    };
    codexCategories?: Record<string, string>;
  };

  const settingsMap = novelData.entitiesMeta?.settings || {};
  if (settingsMap[entityType] && settingsMap[entityType].length > 0) {
    return settingsMap[entityType];
  }

  // If exact entityType key didn't match and it's a codex category, match by category name
  if (entityType.startsWith('codex:')) {
    const categoriesMap = novelData.entitiesMeta?.codexCategories || novelData.codexCategories || {};
    let searchName = categoryNameFallback;

    if (!searchName) {
      const catId = entityType.replace('codex:', '');
      searchName = categoriesMap[catId];
    }

    if (searchName) {
      const matchedCatId = Object.keys(categoriesMap).find(
        (id) => categoriesMap[id]?.trim().toLowerCase() === searchName?.trim().toLowerCase()
      );

      if (matchedCatId) {
        const matchedType = `codex:${matchedCatId}`;
        if (settingsMap[matchedType]) {
          return settingsMap[matchedType];
        }
      }
    }
  }

  return settingsMap[entityType] || [];
}

export async function saveEntitySettings(
  firestore: Firestore,
  ownerId: string,
  novelId: string,
  layout: NovelStorageLayout,
  entityType: string,
  settings: SectionDefinition[]
): Promise<void> {
  const resolver = new StorageResolver(firestore);
  const metaMode = layout?.entities?.[entityType]?.meta || 'embedded';

  const batch = writeBatch(firestore);

  const novelRef = resolver.getNovelRef(ownerId, novelId);

  if (metaMode === 'split') {
    const metaRef = doc(firestore, 'users', ownerId, 'novels', novelId, 'entitiesMeta', entityType);
    batch.set(metaRef, { settings }, mergeOptions);
    // Clean up embedded location to ensure single source of truth
    batch.update(novelRef, {
      [`entitiesMeta.settings.${entityType}`]: deleteField()
    });
  } else {
    batch.update(novelRef, {
      [`entitiesMeta.settings.${entityType}`]: settings
    });
  }

  await batch.commit();
}
